import React, { useMemo, useState } from 'react'

import { Popover, Stack } from '@mui/material'

import { useCompaniesQuery } from 'hooks/api'
import { listedCompanies } from 'utils/listedCompany'
import { Search, SearchSelect } from 'components'

import { FilterField } from './FilterField'
import { filterDropdownPaperSx } from './dropdownSurface'

interface CompanyFilterProps {
  /** Selected company ids. Staged in the drawer, which auto-applies them. */
  value: string[]
  onChange: (ids: string[]) => void
}

/**
 * Cross-company Company filter for internal list views (VERA-882). Mirrors the
 * other filter-drawer search fields exactly — FilterField + inline Search +
 * a Popover dropdown of company previews (SearchSelect, `preview: 'companies'`),
 * multi-select — but the options are ALL Vera companies (from useCompaniesQuery),
 * not just those on the loaded page, so it can drive a server-side company filter.
 * No scan button (companies have no tag to scan).
 */
export const CompanyFilter = ({ value, onChange }: CompanyFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const { data } = useCompaniesQuery({ limit: 200 })
  const companies = useMemo(
    () => listedCompanies(data),
    [data],
  )
  const selectedItems = useMemo(
    () => companies.filter((c: any) => value.includes(c.id)),
    [companies, value],
  )

  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement)
    setOpen(true)
  }
  const handleClose = () => {
    setAnchorEl(null)
    setOpen(false)
  }
  // Report as you tick — the drawer applies on a debounce, so there is nothing to defer to an
  // Apply. Split from the closing variant for the same reason `SearchFilter` splits its own.
  const emitSelection = (items: any[]) => onChange(items.map((c) => c.id))
  const handleApply = (items: any[]) => {
    handleClose()
    emitSelection(items)
  }

  return (
    <FilterField
      label="Company"
      showClear={value.length > 0}
      onClear={() => onChange([])}
    >
      <Stack
        direction="row"
        spacing={2}
        onClick={handleClick}
        sx={{ cursor: 'pointer' }}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Search
            fullWidth
            debounce={false}
            onChange={(v: string) => setQuery(v)}
            onClear={() => setQuery('')}
            placeholder="Search"
            sx={{ minWidth: 0 }}
          />
        </Stack>
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (theme) =>
              filterDropdownPaperSx(
                theme,
                anchorEl ? (anchorEl as HTMLElement).clientWidth : undefined,
                anchorEl as HTMLElement | null,
              ),
          },
        }}
      >
        <SearchSelect
          items={companies}
          selected={selectedItems}
          source="filters"
          hoverGradient
          searchParams={{ fields: ['name'], preview: 'companies' }}
          hideSearch
          externalSearch={query}
          autoApply
          onChange={emitSelection}
          onApply={handleApply}
          // Clear empties the field for real, then closes — it used to only close, which was
          // survivable while Apply existed and is not now that there is no other undo.
          onClear={() => { onChange([]); handleClose() }}
        />
      </Popover>
    </FilterField>
  )
}
